import { createPortal } from 'react-dom';
import { History, X, Clock, RotateCcw, Trash2 } from 'lucide-react';

const CodeHistoryModal = ({ isOpen, onClose, versions, onRestore, onDelete, currentCode, language }) => {
  const isSpanish = language === 'es';
  
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(isSpanish ? 'es-ES' : 'en-US', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const truncateCode = (code, maxLength = 100) => {
    if (!code) return '';
    return code.length > maxLength ? code.substring(0, maxLength) + '...' : code;
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg">
              <History className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">
                {isSpanish ? 'Historial de Versiones' : 'Version History'}
              </h3>
              <p className="text-xs text-zinc-400">
                {versions.length} {isSpanish ? 'versiones guardadas' : 'saved versions'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400 hover:text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="w-12 h-12 text-zinc-600 mb-4" />
              <h4 className="text-lg font-medium text-zinc-300 mb-2">
                {isSpanish ? 'Sin historial aún' : 'No history yet'}
              </h4>
              <p className="text-sm text-zinc-500 max-w-md">
                {isSpanish 
                  ? 'Las versiones anteriores del código aparecerán aquí cuando DamyxIA genere nuevo código'
                  : 'Previous code versions will appear here when DamyxIA generates new code'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Current Version */}
              <div className="p-3 rounded-xl bg-emerald-600/20 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-sm font-medium text-emerald-400">
                      {isSpanish ? 'Versión Actual' : 'Current Version'}
                    </span>
                  </div>
                </div>
                <pre className="text-xs text-zinc-300 font-mono bg-zinc-950/50 p-2 rounded overflow-hidden">
                  {truncateCode(currentCode)}
                </pre>
              </div>

              {/* Previous Versions */}
              {versions.map((version, index) => (
                <div 
                  key={version.id}
                  className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-zinc-500" />
                      <span className="text-sm text-zinc-300">
                        {isSpanish ? 'Versión' : 'Version'} {versions.length - index}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {formatDate(version.timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onRestore(version)}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded-lg transition-colors"
                        title={isSpanish ? 'Restaurar' : 'Restore'}
                      >
                        <RotateCcw className="w-3 h-3" />
                        {isSpanish ? 'Restaurar' : 'Restore'}
                      </button>
                      <button
                        onClick={() => onDelete(version.id)}
                        className="p-1 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title={isSpanish ? 'Eliminar' : 'Delete'}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <pre className="text-xs text-zinc-400 font-mono bg-zinc-950/50 p-2 rounded overflow-hidden">
                    {truncateCode(version.code)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {versions.length > 0 && (
          <div className="px-4 py-3 border-t border-zinc-700 flex justify-end">
            <button
              onClick={() => {
                if (window.confirm(isSpanish ? '¿Eliminar todo el historial?' : 'Delete all history?')) {
                  versions.forEach(v => onDelete(v.id));
                }
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {isSpanish ? 'Limpiar historial' : 'Clear history'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CodeHistoryModal;
